import { useCallback, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';

/**
 * The one table the document list, the journal and the form's line table use.
 * Above `virtualizeThreshold` rows only the visible window is mounted: the
 * engine renders 1 000+ documents without creating 1 000 DOM nodes (spec §7).
 */

export interface GridColumn<Row> {
  key: string;
  label: string;
  align?: 'left' | 'right' | 'center';
  width?: string;
  render(row: Row): ReactNode;
}

export interface DataGridProps<Row> {
  columns: Array<GridColumn<Row>>;
  rows: Row[];
  getRowKey(row: Row): string;
  rowHeight?: number;
  viewportHeight?: number;
  overscan?: number;
  virtualizeThreshold?: number;
  emptyText?: string;
  onRowClick?(row: Row): void;
  initialScrollTop?: number;
  testId?: string;
}

export interface GridWindowInput {
  rowCount: number;
  rowHeight: number;
  viewportHeight: number;
  scrollTop: number;
  overscan: number;
}

export interface GridWindow {
  startIndex: number;
  rows: number;
  totalHeight: number;
  offsetTop: number;
}

export function computeWindow(input: GridWindowInput): GridWindow {
  const { rowCount, rowHeight, viewportHeight, scrollTop, overscan } = input;
  if (rowCount <= 0 || rowHeight <= 0) {
    return { startIndex: 0, rows: 0, totalHeight: 0, offsetTop: 0 };
  }
  const firstVisible = Math.floor(Math.max(scrollTop, 0) / rowHeight);
  const visibleCount = Math.max(1, Math.ceil(viewportHeight / rowHeight));
  const startIndex = Math.max(0, Math.min(firstVisible, rowCount - 1) - Math.max(overscan, 0));
  const endIndex = Math.min(rowCount, firstVisible + visibleCount + Math.max(overscan, 0));
  return {
    startIndex,
    rows: Math.max(0, endIndex - startIndex),
    totalHeight: rowCount * rowHeight,
    offsetTop: startIndex * rowHeight,
  };
}

function alignOf(value: GridColumn<unknown>['align']): CSSProperties['textAlign'] {
  return value ?? 'left';
}

export function DataGrid<Row>({
  columns,
  rows,
  getRowKey,
  rowHeight = 36,
  viewportHeight = 420,
  overscan = 4,
  virtualizeThreshold = 200,
  emptyText = '',
  onRowClick,
  initialScrollTop = 0,
  testId,
}: DataGridProps<Row>): JSX.Element {
  const [scrollTop, setScrollTop] = useState(initialScrollTop);
  const virtualize = rows.length > virtualizeThreshold;
  const window = virtualize
    ? computeWindow({ rowCount: rows.length, rowHeight, viewportHeight, scrollTop, overscan })
    : { startIndex: 0, rows: rows.length, totalHeight: rows.length * rowHeight, offsetTop: 0 };

  const onScroll = useCallback((event: { currentTarget: { scrollTop: number } }) => {
    setScrollTop(event.currentTarget.scrollTop);
  }, []);

  const visible = virtualize ? rows.slice(window.startIndex, window.startIndex + window.rows) : rows;

  const cellStyle: CSSProperties = {
    padding: '8px 12px',
    borderBottom: '1px solid var(--line)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  return (
    <div data-testid={testId ?? 'data-grid'} style={{ border: '1px solid var(--line)', borderRadius: 'var(--radius)' }}>
      <div
        role="row"
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))`,
          background: 'var(--surface-alt, var(--surface))',
          borderBottom: '1px solid var(--line)',
          fontSize: 11,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          color: 'var(--dim)',
        }}
      >
        {columns.map((column) => (
          <div key={column.key} style={{ ...cellStyle, textAlign: alignOf(column.align), width: column.width }}>
            {column.label}
          </div>
        ))}
      </div>

      {rows.length === 0 ? (
        <p className="muted" style={{ padding: 24, textAlign: 'center' }}>
          {emptyText}
        </p>
      ) : (
        <div
          onScroll={onScroll}
          style={{
            height: virtualize ? viewportHeight : undefined,
            overflowY: virtualize ? 'auto' : undefined,
            position: 'relative',
          }}
        >
          <div style={{ height: window.totalHeight, position: 'relative' }}>
            <div style={{ position: 'absolute', top: window.offsetTop, left: 0, right: 0 }}>
              {visible.map((row) => (
                <div
                  key={getRowKey(row)}
                  role="row"
                  data-grid-row="true"
                  onClick={onRowClick === undefined ? undefined : () => onRowClick(row)}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))`,
                    cursor: onRowClick === undefined ? 'default' : 'pointer',
                    height: rowHeight,
                  }}
                >
                  {columns.map((column) => (
                    <div key={column.key} style={{ ...cellStyle, textAlign: alignOf(column.align) }}>
                      {column.render(row)}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
